// "label (url)", or the bare url when the label is empty or the url itself.
// The url stays visible: users search files for links.
const formatLink = (label: string, url: string, closed = true): string => {
  const text = label.trim();
  if (!url) return text;
  if (!text || text === url) return url;
  return `${text} (${url}${closed ? ')' : ''}`;
};

/**
 * Snippets are cut from the markdown files were converted to at ingestion.
 * Show them as plain text: links as "label (url)", other syntax dropped.
 * A snippet's "…" ends can cut a link in half, so half links are formatted too.
 */
export const formatSnippet = (snippet: string): string =>
  snippet
    // Autolinks: <https://acme.com> -> https://acme.com
    .replace(/<((?:https?:\/\/|mailto:)[^>\s]+)>/g, '$1')
    // Links and images: [label](url "title") -> label (url)
    .replace(
      /!?\[([^\]]*)\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/g,
      (_, label: string, url: string) => formatLink(label, url)
    )
    // Link cut at the start: …label](url) -> …label (url)
    .replace(
      /^(…?)([^[\]]*)\]\(\s*<?([^)\s>]*)>?\s*\)/,
      (_, cut: string, label: string, url: string) =>
        `${cut}${formatLink(label, url)}`
    )
    // Link cut at the end: [label](url… -> label (url…
    .replace(
      /!?\[([^\]]*)\]\(\s*<?([^)\s>]*)(…?)$/,
      (_, label: string, url: string, cut: string) =>
        `${formatLink(label, url, false)}${cut}`
    )
    // Bold and italics: **text**, __text__, *text*, _text_ -> text.
    // Only around words, so snake_case names, urls and \* escapes keep theirs.
    .replace(/(^|[^\\])(\*\*|__)(\S(?:.*?[^\s\\])?)\2/g, '$1$3')
    .replace(/(^|[^\w*\\])\*(\S(?:[^*]*?[^\s\\])?)\*(?![\w*])/g, '$1$2')
    .replace(/(^|[^\w\\])_(\S(?:[^_]*?[^\s\\])?)_(?!\w)/g, '$1$2')
    // Inline code and markdown escapes: `code` -> code, \* -> *
    .replace(/`+/g, '')
    .replace(/\\([\\`*_{}[\]()#+\-.!|>])/g, '$1')
    // Heading, quote and list markers. The backend joins lines with spaces,
    // so their markers sit mid-snippet: "intro ## Pricing" -> "intro Pricing"
    .replace(/(^|\s)(?:#{1,6}|>+|[*+])(?=\s)/g, '$1')
    // Tables: "| a | b |" and "|---|:---:|" rows
    .replace(/\|?\s*:?-{3,}:?\s*(?=\|)/g, '')
    .replace(/\s*\|[\s|]*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
